/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { SortPayload, useDataProvider } from 'react-admin';
import { AutocompleteArrayInput } from 'react-admin';

interface CmsAutocompleteArrayInputProps {
  source: string;
  label: string;
  sourceUrl: string;
  validate?: never;
  initialSuggestions: Suggestion[];
  sort: SortPayload;
  filterToQuery: Function;
  optionText: Function;
}

interface Suggestion {
  // The ID is the option.
  // React-Admin needs each entity in a "list" response (e.g.,
  // /companies) to have an ID property,
  // but since each option should be unique anyway,
  // there is no point in having a separate ID and separate option value.
  id: string;
  // Matches the ID - needed to prevent duplicate options from appearing
  // in the search list
  key: string;
}

/**
 * Using this to get the full list of options
 * that the CMS user can browse to get the option they want.
 * The option list gets filtered as the user types more characters.
 */
const OPTION_PAGE_SIZE = Number.MAX_SAFE_INTEGER;

/**
 * Created this component to solve the problem that we were facing
 * with ReferenceArrayInput over AutocompleteArrayInput, namely
 * that when typing in a search term for a second selection, it would
 * wipe all existing selections
 * https://github.com/marmelab/react-admin/issues/8181
 * (The clearOnBlur fix mentioned in the Github issue didn't help)
 */
export const CmsCustomMultipleSelectionInput = (props: CmsAutocompleteArrayInputProps) => {
  const { source, sourceUrl, initialSuggestions, label, sort, filterToQuery, optionText } = props;
  const [suggestions, setSuggestions] = useState<Suggestion[]>(initialSuggestions);

  // Load full set suggestions upon page load
  // So far this seems to work well but may struggle on higher loads
  // (e.g. 10,000+)
  useEffect(() => {
    fetchSuggestions('');
  }, []);

  let apiSuggestions: { id: string }[] = [];

  const dataProvider = useDataProvider();

  // Fetches suggestions from API, then consolidates with
  // existing suggestions. This prevents override of existing
  // selections.
  const fetchSuggestions = async (inputValue: string) => {
    const { data } = await dataProvider.getList(sourceUrl, {
      pagination: { page: 1, perPage: OPTION_PAGE_SIZE },
      sort: sort,
      filter: filterToQuery(inputValue),
    });
    apiSuggestions = data;

    if (apiSuggestions.length > 0) {
      const newSuggestions = apiSuggestions.filter(
        (apiSuggestion) => !suggestions.some((suggestion: Suggestion) => suggestion.id === apiSuggestion.id)
      );
      // add key property based on the suggestion's ID
      const suggestionsWithKeys = newSuggestions.map((suggestion) => ({
        ...suggestion,
        key: suggestion.id,
      }));
      setSuggestions([...suggestions, ...suggestionsWithKeys]);
    }
  };

  return (
    <AutocompleteArrayInput
      variant="outlined"
      helperText={false}
      autoHighlight
      label={label}
      choices={suggestions}
      setFilter={fetchSuggestions}
      fullWidth
      blurOnSelect={false}
      optionText={optionText}
      format={(values: any) => values && values.map((v: any) => v.id)}
      source={source}
      parse={(value) => value && value.map((v: any) => ({ id: v }))}
    />
  );
};
